// Module: LessonContentDrawer
// -----------------------------------------------------------------
// Receives:  a lesson (with or without slide_data), open state, assign hook
// Produces:  the "Lesson details" drawer and the slide preview modal
// Owns:      /slides + /assessment-pages fetch, preview selection
// Does NOT:  assignment itself (→ caller via onAssign),
//            row-level library actions such as the calculator (→ LessonCard)
// -----------------------------------------------------------------
import WorkspacePrimaryButton from "@/components/PrimaryButton";
import LessonDrawer from "@/components/workspace/LessonDrawer";
import { isAssessmentSlideType } from "@/features/tutor/constants/assessmentTypes";
import type { Lesson } from "@/types/tutor";
import api from "@/utils/axios-utils";
import { evictPracticeDiagrams } from "@/components/practiceDiagram/practiceDiagramLoader";
import { FileText, Users } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import SlideHtmlPreviewModal, { PreviewSlide } from "./SlideHtmlPreviewModal";
import SlideItem from "./SlideItem";
import {
  biteNumbersByIndex,
  formatLessonUpdatedAt,
  LessonRow,
  orderLessonRows,
  PreviewFetchStatus,
} from "./lessonRows";

interface AssessmentPage {
  slide_index: number;
  survey_json: Record<string, unknown> | null;
}

type FetchedSlide = PreviewSlide & {
  metadata?: { slide_type?: string } | null;
};

/**
 * Input:   lesson — library lessons carry slide_data; group lessons do not, so
 *          their rows come from the /slides response instead.
 *          isOpen / onToggle — the drawer's open state, owned by the caller.
 *          onAssign — omitted where the lesson cannot be assigned from here.
 * Output:  the drawer (while open) and the preview modal (always mounted).
 */
const LessonContentDrawer = ({
  lesson,
  isOpen,
  onToggle,
  onAssign,
}: {
  lesson: Lesson & { slide_data?: LessonRow[] };
  isOpen: boolean;
  onToggle: () => void;
  onAssign?: () => void;
}) => {
  const displayName = lesson.name.trim() || "Untitled lesson";
  const [previewSlides, setPreviewSlides] = useState<PreviewSlide[]>([]);
  const [slidesStatus, setSlidesStatus] =
    useState<PreviewFetchStatus>("loading");
  const [surveysStatus, setSurveysStatus] =
    useState<PreviewFetchStatus>("loading");
  const [retryToken, setRetryToken] = useState(0);
  const [previewSlideIndex, setPreviewSlideIndex] = useState<number | null>(
    null,
  );

  const hasSlideData = lesson.slide_data !== undefined;
  const orderedRows = orderLessonRows<LessonRow>(
    lesson.slide_data ??
      previewSlides.map((slide) => ({
        index: slide.slide_index,
        name: slide.name ?? `Content item ${slide.slide_index + 1}`,
        slide_type: slide.slide_type,
      })),
  );
  const biteNumbers = biteNumbersByIndex(orderedRows);
  const previewByIndex = new Map(previewSlides.map((s) => [s.slide_index, s]));
  // Resolved on every render: the preview can open before /slides answers.
  const previewPosition = Math.max(
    0,
    previewSlides.findIndex((s) => s.slide_index === previewSlideIndex),
  );

  const openPreviewAt = (slideIndex: number) => {
    if (isOpen) onToggle();
    setPreviewSlideIndex(slideIndex);
  };

  const rowNamesRef = useRef<Record<number, string>>({});
  rowNamesRef.current = Object.fromEntries(
    (lesson.slide_data ?? []).map((row) => [row.index, row.name]),
  );

  const retryPreviewData = () => setRetryToken((token) => token + 1);

  // Opening the preview collapses the drawer; keep the fetch alive across that
  // hand-off so a preview opened mid-load still receives its slides.
  const isPreviewDataNeeded = isOpen || previewSlideIndex !== null;

  useEffect(() => {
    if (!isPreviewDataNeeded) return;
    let cancelled = false;
    setSlidesStatus("loading");
    setSurveysStatus("loading");

    // allSettled, not all: a failing survey fetch must not blank the bites, and
    // must stay distinguishable from a lesson that simply has no pages.
    Promise.allSettled([
      api.get(`/lessons/${lesson.id}/slides`),
      api.get(`/lessons/${lesson.id}/assessment-pages`),
    ]).then(([slidesRes, pagesRes]) => {
      if (cancelled) return;

      const pagesByIndex: Record<number, AssessmentPage> = {};
      if (pagesRes.status === "fulfilled") {
        const pages = (pagesRes.value.data?.pages ?? []) as AssessmentPage[];
        pages.forEach((page) => {
          pagesByIndex[page.slide_index] = page;
        });
      }
      setSurveysStatus(pagesRes.status === "fulfilled" ? "loaded" : "error");

      if (slidesRes.status !== "fulfilled") {
        setSlidesStatus("error");
        setPreviewSlides([]);
        return;
      }
      const slides = (slidesRes.value.data?.slides ?? []) as FetchedSlide[];
      // Fresh urls mean fresh diagrams: one url can be overwritten in place.
      evictPracticeDiagrams(
        slides.flatMap((slide) =>
          slide.practice_host_html ? [slide.practice_host_html] : [],
        ),
      );
      setPreviewSlides(
        [...slides]
          .sort((a, b) => a.slide_index - b.slide_index)
          .map((slide) => ({
            ...slide,
            name: rowNamesRef.current[slide.slide_index] ?? slide.name,
            slide_type: slide.metadata?.slide_type ?? null,
            survey_json: pagesByIndex[slide.slide_index]?.survey_json ?? null,
          })),
      );
      setSlidesStatus("loaded");
    });
    return () => {
      cancelled = true;
    };
  }, [isPreviewDataNeeded, lesson.id, retryToken]);

  const updatedAt = formatLessonUpdatedAt(lesson.updated_at);

  return (
    <>
      {isOpen && (
        <LessonDrawer
          title={displayName}
          onClose={onToggle}
          className="et-library-lesson-drawer"
        >
          <div className="et-library-detail-icon" aria-hidden="true">
            <FileText size={23} />
          </div>
          <p className="et-library-detail-label">Lesson details</p>
          <h2>{displayName}</h2>
          <p className="et-drawer-meta">
            {hasSlideData || slidesStatus === "loaded"
              ? `${orderedRows.length} content items`
              : "Content items"}
            {updatedAt ? ` · Updated ${updatedAt}` : ""}
          </p>
          {onAssign && (
            <WorkspacePrimaryButton
              className="et-primary-button"
              onClick={() => {
                onToggle();
                onAssign();
              }}
            >
              <Users size={16} />
              Assign to a group
            </WorkspacePrimaryButton>
          )}
          <div className="et-drawer-content">
            <h3>Lesson content</h3>
            {orderedRows.map((slide) => (
              <SlideItem
                key={slide.index}
                index={slide.index}
                displayNumber={biteNumbers[slide.index]}
                slideName={slide.name}
                slideType={slide.slide_type}
                surveyJson={
                  previewByIndex.get(slide.index)?.survey_json ?? null
                }
                lessonId={lesson.id}
                slideHtmlUrl={
                  previewByIndex.get(slide.index)?.slide_html ?? null
                }
                practiceHostHtmlUrl={
                  previewByIndex.get(slide.index)?.practice_host_html ?? null
                }
                sourceStatus={
                  isAssessmentSlideType(slide.slide_type)
                    ? surveysStatus
                    : slidesStatus
                }
                onRetry={retryPreviewData}
                onPreview={openPreviewAt}
              />
            ))}
            {orderedRows.length === 0 &&
              (hasSlideData || slidesStatus === "loaded" ? (
                <p className="et-empty">No lesson content available.</p>
              ) : slidesStatus === "loading" ? (
                <p role="status" className="et-empty">
                  Loading lesson content…
                </p>
              ) : (
                <p role="alert" className="et-group-error">
                  Could not load lesson content.{" "}
                  <button type="button" onClick={retryPreviewData}>
                    Try again
                  </button>
                </p>
              ))}
          </div>
        </LessonDrawer>
      )}
      {/* Portalled: group rows mount this drawer inside a <tbody>, where the
          modal's fixed overlay would be invalid table content. */}
      {createPortal(
        <SlideHtmlPreviewModal
          isOpen={previewSlideIndex !== null}
          onClose={() => setPreviewSlideIndex(null)}
          lessonName={displayName}
          slides={previewSlides}
          surveysStatus={surveysStatus}
          onRetrySurveys={retryPreviewData}
          initialIndex={previewPosition}
          onAssign={
            onAssign
              ? () => {
                  setPreviewSlideIndex(null);
                  onAssign();
                }
              : undefined
          }
        />,
        document.body,
      )}
    </>
  );
};

export default LessonContentDrawer;
